import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { inputToKg, ftInToCm, detectMeasurementSystem } from '../utils/weightUnit';
import { ChevronRight, ChevronLeft, Dumbbell, Apple, Target, Sparkles, Check, AlertTriangle, Loader2, Menu, ArrowLeft, LogOut, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MUSCLE_GROUPS = [
  { id: 'chest', label: 'Chest', icon: '💪' },
  { id: 'back', label: 'Back', icon: '🔙' },
  { id: 'biceps', label: 'Biceps', icon: '💪' },
  { id: 'triceps', label: 'Triceps', icon: '💪' },
  { id: 'shoulders', label: 'Shoulders', icon: '🤸' },
  { id: 'legs', label: 'Legs', icon: '🦵' },
  { id: 'core', label: 'Core / Abs', icon: '🎯' },
  { id: 'glutes', label: 'Glutes', icon: '🍑' },
];

const FITNESS_GOALS = [
  { id: 'lose_weight', label: 'Lose Weight', desc: 'Burn fat and slim down' },
  { id: 'build_muscle', label: 'Build Muscle', desc: 'Gain size and strength' },
  { id: 'body_recomp', label: 'Body Recomposition', desc: 'Lose fat & gain muscle' },
  { id: 'maintain', label: 'Maintain Weight', desc: 'Stay where you are' },
  { id: 'general_health', label: 'General Health', desc: 'Overall wellness & energy' },
];

const DIETARY_PREFS = [
  { id: 'none', label: 'No Restrictions' },
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'vegan', label: 'Vegan' },
  { id: 'pescatarian', label: 'Pescatarian' },
  { id: 'keto', label: 'Keto' },
  { id: 'paleo', label: 'Paleo' },
  { id: 'mediterranean', label: 'Mediterranean' },
];

const FOOD_ALLERGIES = [
  { id: 'none', label: 'None' },
  { id: 'gluten', label: 'Gluten' },
  { id: 'dairy', label: 'Dairy' },
  { id: 'nuts', label: 'Nuts' },
  { id: 'soy', label: 'Soy' },
  { id: 'eggs', label: 'Eggs' },
  { id: 'shellfish', label: 'Shellfish' },
];

const ProfileSetup = ({ onComplete, onLogout }) => {
  const { t } = useTranslation();
  const detectedSystem = detectMeasurementSystem();
  const [step, setStep] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const { token, fetchUser } = useAuth();

  const [profile, setProfile] = useState({
    // Step 1: Body Info
    height: '', height_ft: '', height_in: '',
    weight: '', age: '', gender: 'male',
    activity_level: 'moderate', goal_weight: '',
    measurement_system: detectedSystem,
    // Step 2: Fitness Goals (multi-select)
    fitness_goal: '',
    fitness_goals: [],
    target_muscles: [],
    training_experience: 'beginner',
    workout_days_per_week: 3,
    injuries_limitations: '',
    // Step 3: Dietary Preferences
    dietary_preference: 'none',
    food_allergies: [],
    prefers_organic: false,
    meals_per_day: 3,
  });

  const [profileSaved, setProfileSaved] = useState(false);

  useEffect(() => {
    const checkProfile = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const userData = response.data.user || response.data;
        if (userData?.profile && !profileSaved) {
          await fetchUser();
          onComplete();
          return;
        }
      } catch (err) {
        console.error('Profile check failed:', err);
      }
      setChecking(false);
    };
    if (token) checkProfile();
    else setChecking(false);
  }, []);

  const system = profile.measurement_system;
  const weightUnit = system === 'imperial' ? 'lbs' : 'kg';
  const totalSteps = 4;

  const toggleFitnessGoal = (goalId) => {
    setProfile(prev => {
      const next = prev.fitness_goals.includes(goalId)
        ? prev.fitness_goals.filter(g => g !== goalId)
        : [...prev.fitness_goals, goalId];
      // keep legacy single-value field in sync (= first selected goal) for backward compat
      return { ...prev, fitness_goals: next, fitness_goal: next[0] || '' };
    });
  };

  const toggleMuscle = (muscleId) => {
    setProfile(prev => ({
      ...prev,
      target_muscles: prev.target_muscles.includes(muscleId)
        ? prev.target_muscles.filter(m => m !== muscleId)
        : [...prev.target_muscles, muscleId]
    }));
  };

  const toggleAllergy = (allergyId) => {
    if (allergyId === 'none') {
      setProfile(prev => ({ ...prev, food_allergies: ['none'] }));
      return;
    }
    setProfile(prev => ({
      ...prev,
      food_allergies: prev.food_allergies.includes(allergyId)
        ? prev.food_allergies.filter(a => a !== allergyId)
        : [...prev.food_allergies.filter(a => a !== 'none'), allergyId]
    }));
  };

  const validateStep = () => {
    if (step === 1) {
      const missing = [];
      if (system === 'imperial') {
        if (!profile.height_ft) missing.push(t('profile.height') + ' (ft)');
        if (profile.height_in === '' || profile.height_in === null || profile.height_in === undefined) missing.push(t('profile.height') + ' (in)');
      } else {
        if (!profile.height) missing.push(t('profile.height'));
      }
      if (!profile.weight) missing.push(t('profile.weight'));
      if (!profile.age) missing.push(t('profile.age'));
      return missing;
    }
    if (step === 2) {
      if (profile.fitness_goals.length === 0 && !profile.fitness_goal) return ['Primary fitness goal'];
    }
    return [];
  };

  const canProceed = () => validateStep().length === 0;

  const handleSaveProfile = async () => {
    setError('');
    try {
      const weightInKg = inputToKg(parseFloat(profile.weight), weightUnit);
      const goalWeightInKg = profile.goal_weight ? inputToKg(parseFloat(profile.goal_weight), weightUnit) : null;
      const heightInCm = system === 'imperial'
        ? ftInToCm(profile.height_ft, profile.height_in)
        : parseFloat(profile.height);

      const profileData = {
        height: heightInCm,
        weight: weightInKg,
        age: parseInt(profile.age),
        gender: profile.gender,
        activity_level: profile.activity_level,
        goal_weight: goalWeightInKg,
        weight_unit: weightUnit,
        measurement_system: system,
        fitness_goal: profile.fitness_goal || (profile.fitness_goals[0] || null),
        fitness_goals: profile.fitness_goals.length > 0 ? profile.fitness_goals : null,
        target_muscles: profile.target_muscles.length > 0 ? profile.target_muscles : null,
        training_experience: profile.training_experience,
        workout_days_per_week: profile.workout_days_per_week,
        dietary_preference: profile.dietary_preference,
        food_allergies: profile.food_allergies.length > 0 ? profile.food_allergies : null,
        prefers_organic: profile.prefers_organic,
        meals_per_day: profile.meals_per_day,
        injuries_limitations: profile.injuries_limitations || null,
      };

      await axios.post(`${API}/profile/update`, { profile: profileData }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Don't call fetchUser() here - wait until user finishes Step 4
      return true;
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Failed to update profile');
      return false;
    }
  };

  const handleComplete = async () => {
    await fetchUser();
    onComplete();
  };

  const handleGeneratePlan = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await axios.post(`${API}/plan/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedPlan(res.data.plan);
    } catch (err) {
      setError('Failed to generate plan. You can generate it later from your dashboard.');
    } finally {
      setGenerating(false);
    }
  };

  const handleNext = async () => {
    const missing = validateStep();
    if (missing.length > 0) {
      setError(`Please fill in: ${missing.join(', ')}`);
      return;
    }
    setError('');
    if (step === 3) {
      const saved = await handleSaveProfile();
      if (saved) {
        setProfileSaved(true);
        setStep(4);
      }
    } else if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100 p-4">
        <div className="text-center">
          <img src="/logo-v4.png" alt="Logo" className="w-24 h-24 object-contain mx-auto mb-6 animate-pulse" />
          <div className="text-lg text-gray-600 dark:text-gray-300 font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 p-4 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#FF6600] to-[#FF8533] p-5 relative">
          {/* Top-left hamburger menu — gives users a way out of the profile flow */}
          {step < 4 && (
            <button
              data-testid="profile-setup-hamburger"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="absolute top-3 left-3 w-10 h-10 rounded-lg bg-white/20 hover:bg-white/30 active:bg-white/40 flex items-center justify-center text-white transition-colors"
            >
              <Menu size={22} />
            </button>
          )}
          <div className="flex items-center justify-center mb-3">
            <img src="/logo-v4.png" alt="Logo" className="h-12 w-12 object-contain" />
          </div>
          <h1 className="text-xl font-bold text-white text-center">
            {step === 1 && 'Your Body Profile'}
            {step === 2 && 'Fitness Goals'}
            {step === 3 && 'Dietary Preferences'}
            {step === 4 && 'Your Personalized Plan'}
          </h1>
          <p className="text-orange-100 text-center text-sm mt-1">
            {step === 1 && 'Tell us about yourself'}
            {step === 2 && 'What do you want to achieve?'}
            {step === 3 && 'How do you prefer to eat?'}
            {step === 4 && 'AI-powered plan just for you'}
          </p>
          {/* Progress */}
          <div className="flex gap-1.5 mt-4 justify-center">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className={`h-1.5 rounded-full transition-all ${s <= step ? 'bg-white dark:bg-gray-800 w-10' : 'bg-white dark:bg-gray-800/30 w-6'}`} />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {/* STEP 1: Body Info */}
          {step === 1 && (
            <div className="space-y-4 animate-fade-in">
              {/* Measurement System Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 text-center sm:text-left">{t('profile.measurementSystem')}:</span>
                <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden self-center" data-testid="measurement-system-toggle">
                  <button type="button" onClick={() => setProfile({ ...profile, measurement_system: 'metric' })}
                    className={`px-5 py-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${system === 'metric' ? 'bg-[#FF6600] text-white' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                    data-testid="setup-metric-btn">{t('profile.metric')}</button>
                  <button type="button" onClick={() => setProfile({ ...profile, measurement_system: 'imperial' })}
                    className={`px-5 py-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${system === 'imperial' ? 'bg-[#FF6600] text-white' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                    data-testid="setup-imperial-btn">{t('profile.imperial')}</button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {system === 'imperial' ? (
                  <div className="col-span-2 grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-sm font-medium block mb-1 text-gray-700 dark:text-gray-300">{t('profile.height')} (ft)</label>
                      <input type="number" step="1" min="1" max="8" data-testid="profile-height-ft-input"
                        value={profile.height_ft}
                        onChange={(e) => setProfile({ ...profile, height_ft: e.target.value })}
                        className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-[#FF6600] bg-white dark:bg-gray-700 dark:text-white"
                        placeholder="5" />
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-1 text-gray-700 dark:text-gray-300">{t('profile.height')} (in)</label>
                      <input type="number" step="1" min="0" max="11" data-testid="profile-height-in-input"
                        value={profile.height_in}
                        onChange={(e) => setProfile({ ...profile, height_in: e.target.value })}
                        className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-[#FF6600] bg-white dark:bg-gray-700 dark:text-white"
                        placeholder="9" />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="text-sm font-medium block mb-1 text-gray-700 dark:text-gray-300">{t('profile.height')} (cm)</label>
                    <input type="number" step="0.1" min="1" data-testid="profile-height-input"
                      value={profile.height}
                      onChange={(e) => setProfile({ ...profile, height: e.target.value })}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-[#FF6600] bg-white dark:bg-gray-700 dark:text-white" />
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium block mb-1 text-gray-700 dark:text-gray-300">{t('profile.weight')} ({weightUnit})</label>
                  <input type="number" step="0.1" min="1" data-testid="profile-weight-input"
                    value={profile.weight}
                    onChange={(e) => setProfile({ ...profile, weight: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-[#FF6600] bg-white dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1 text-gray-700 dark:text-gray-300">{t('profile.age')}</label>
                  <input type="number" step="1" min="1" max="120" data-testid="profile-age-input"
                    value={profile.age}
                    onChange={(e) => setProfile({ ...profile, age: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-[#FF6600] bg-white dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1 text-gray-700 dark:text-gray-300">{t('profile.gender')}</label>
                  <select data-testid="profile-gender-select" value={profile.gender}
                    onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-[#FF6600] bg-white dark:bg-gray-700 dark:text-white">
                    <option value="male">{t('profile.male')}</option>
                    <option value="female">{t('profile.female')}</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium block mb-1 text-gray-700 dark:text-gray-300">{t('profile.activityLevel')}</label>
                  <select data-testid="profile-activity-select" value={profile.activity_level}
                    onChange={(e) => setProfile({ ...profile, activity_level: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-[#FF6600] bg-white dark:bg-gray-700 dark:text-white">
                    <option value="sedentary">{t('profile.sedentary')}</option>
                    <option value="light">{t('profile.light')}</option>
                    <option value="moderate">{t('profile.moderate')}</option>
                    <option value="active">{t('profile.active')}</option>
                    <option value="very_active">{t('profile.veryActive')}</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium block mb-1 text-gray-700 dark:text-gray-300">{t('profile.goalWeight')} ({weightUnit}) - {t('profile.optional')}</label>
                  <input type="number" step="0.1" min="1" data-testid="profile-goal-weight-input"
                    value={profile.goal_weight}
                    onChange={(e) => setProfile({ ...profile, goal_weight: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-[#FF6600] bg-white dark:bg-gray-700 dark:text-white" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Fitness Goals */}
          {step === 2 && (
            <div className="space-y-5 animate-fade-in">
              {/* Fitness Goal */}
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-3">
                  <Target size={16} className="text-[#FF6600]" />
                  What are your goals? <span className="text-xs font-normal text-gray-400">(select one or more)</span>
                </label>
                <div className="space-y-2">
                  {FITNESS_GOALS.map(goal => {
                    const isSelected = profile.fitness_goals.includes(goal.id);
                    return (
                      <button key={goal.id} type="button"
                        onClick={() => toggleFitnessGoal(goal.id)}
                        data-testid={`goal-${goal.id}`}
                        className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left ${
                          isSelected
                            ? 'border-[#FF6600] bg-orange-50 dark:bg-orange-900/20'
                            : 'border-gray-200 dark:border-gray-700 dark:border-gray-600 hover:border-gray-300'
                        }`}>
                        <div>
                          <span className="font-medium text-sm text-gray-900 dark:text-white">{goal.label}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">{goal.desc}</span>
                        </div>
                        {isSelected && <Check size={18} className="text-[#FF6600]" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Target Muscles */}
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-3">
                  <Dumbbell size={16} className="text-[#FF6600]" />
                  Target muscle groups <span className="text-xs font-normal text-gray-400">(select all that apply)</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {MUSCLE_GROUPS.map(muscle => (
                    <button key={muscle.id} type="button"
                      onClick={() => toggleMuscle(muscle.id)}
                      data-testid={`muscle-${muscle.id}`}
                      className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all text-left text-sm ${
                        profile.target_muscles.includes(muscle.id)
                          ? 'border-[#FF6600] bg-orange-50 dark:bg-orange-900/20'
                          : 'border-gray-200 dark:border-gray-700 dark:border-gray-600 hover:border-gray-300'
                      }`}>
                      <span className="text-base">{muscle.icon}</span>
                      <span className="font-medium text-gray-800 dark:text-gray-100 dark:text-gray-200">{muscle.label}</span>
                      {profile.target_muscles.includes(muscle.id) && <Check size={14} className="text-[#FF6600] ml-auto" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Training Experience */}
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">Training Experience</label>
                <div className="flex gap-2">
                  {['beginner', 'intermediate', 'advanced'].map(exp => (
                    <button key={exp} type="button"
                      onClick={() => setProfile({ ...profile, training_experience: exp })}
                      data-testid={`exp-${exp}`}
                      className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-medium capitalize transition-all ${
                        profile.training_experience === exp
                          ? 'border-[#FF6600] bg-orange-50 dark:bg-orange-900/20 text-[#FF6600]'
                          : 'border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                      }`}>
                      {exp}
                    </button>
                  ))}
                </div>
              </div>

              {/* Workout Days */}
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
                  Workout days per week: <span className="text-[#FF6600]">{profile.workout_days_per_week}</span>
                </label>
                <input type="range" min="1" max="7" value={profile.workout_days_per_week}
                  onChange={(e) => setProfile({ ...profile, workout_days_per_week: parseInt(e.target.value) })}
                  data-testid="workout-days-slider"
                  className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-[#FF6600]" />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1 day</span><span>7 days</span>
                </div>
              </div>

              {/* Injuries */}
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <AlertTriangle size={14} className="text-amber-500" />
                  Injuries or limitations <span className="text-xs font-normal text-gray-400">(optional)</span>
                </label>
                <input type="text" value={profile.injuries_limitations}
                  onChange={(e) => setProfile({ ...profile, injuries_limitations: e.target.value })}
                  data-testid="injuries-input"
                  placeholder="e.g., bad knee, lower back pain..."
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-[#FF6600] bg-white dark:bg-gray-700 dark:text-white" />
              </div>
            </div>
          )}

          {/* STEP 3: Dietary Preferences */}
          {step === 3 && (
            <div className="space-y-5 animate-fade-in">
              {/* Diet Type */}
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-3">
                  <Apple size={16} className="text-[#FF6600]" />
                  Dietary preference
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {DIETARY_PREFS.map(diet => (
                    <button key={diet.id} type="button"
                      onClick={() => setProfile({ ...profile, dietary_preference: diet.id })}
                      data-testid={`diet-${diet.id}`}
                      className={`p-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                        profile.dietary_preference === diet.id
                          ? 'border-[#FF6600] bg-orange-50 dark:bg-orange-900/20 text-[#FF6600]'
                          : 'border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                      }`}>
                      {diet.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Allergies */}
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 block">
                  Food allergies or sensitivities
                </label>
                <div className="flex flex-wrap gap-2">
                  {FOOD_ALLERGIES.map(allergy => (
                    <button key={allergy.id} type="button"
                      onClick={() => toggleAllergy(allergy.id)}
                      data-testid={`allergy-${allergy.id}`}
                      className={`px-3 py-2 rounded-full border-2 text-xs font-medium transition-all ${
                        profile.food_allergies.includes(allergy.id)
                          ? 'border-[#FF6600] bg-orange-50 dark:bg-orange-900/20 text-[#FF6600]'
                          : 'border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                      }`}>
                      {allergy.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Organic */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 hover:border-[#FF6600] transition-colors">
                  <input type="checkbox" checked={profile.prefers_organic}
                    onChange={(e) => setProfile({ ...profile, prefers_organic: e.target.checked })}
                    data-testid="organic-checkbox"
                    className="w-5 h-5 rounded border-gray-300 text-[#FF6600] focus:ring-[#FF6600] accent-[#FF6600]" />
                  <div>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100 dark:text-gray-200">I prefer organic foods</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block">Meal plans will prioritize organic ingredients</span>
                  </div>
                </label>
              </div>

              {/* Meals per day */}
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
                  Meals per day: <span className="text-[#FF6600]">{profile.meals_per_day}</span>
                </label>
                <div className="flex gap-2">
                  {[2, 3, 4, 5, 6].map(n => (
                    <button key={n} type="button"
                      onClick={() => setProfile({ ...profile, meals_per_day: n })}
                      data-testid={`meals-${n}`}
                      className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-bold transition-all ${
                        profile.meals_per_day === n
                          ? 'border-[#FF6600] bg-orange-50 dark:bg-orange-900/20 text-[#FF6600]'
                          : 'border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                      }`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: AI Plan Generation */}
          {step === 4 && (
            <div className="space-y-5 animate-fade-in">
              {!generatedPlan && !generating && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-[#FF6600] to-[#FF8533] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles size={28} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Your Profile is Ready!</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                    Would you like our AI to create a <strong>personalized workout + meal plan</strong> just for you?
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">
                    It only takes a few seconds. You can also generate it later from your Dashboard.
                    {profile.fitness_goal === 'build_muscle' && profile.target_muscles.length > 0 && (
                      <span className="block mt-2 text-[#FF6600]">
                        Focused on: {profile.target_muscles.map(m => m.charAt(0).toUpperCase() + m.slice(1)).join(', ')}
                      </span>
                    )}
                  </p>
                  <button
                    onClick={handleGeneratePlan}
                    data-testid="generate-plan-btn"
                    className="w-full py-3.5 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 text-base">
                    <Sparkles size={18} />
                    Yes, generate my plan
                  </button>
                  <button
                    onClick={handleComplete}
                    data-testid="skip-plan-btn"
                    className="w-full mt-3 py-2 text-gray-500 dark:text-gray-400 text-sm hover:text-gray-700 dark:text-gray-300 transition-colors">
                    No, maybe later — take me to the Dashboard
                  </button>
                </div>
              )}

              {generating && (
                <div className="text-center py-8">
                  <Loader2 size={40} className="text-[#FF6600] animate-spin mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Creating Your Plan...</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Our AI is analyzing your profile and building a personalized program</p>
                </div>
              )}

              {generatedPlan && (
                <div className="space-y-4">
                  <div className="text-center mb-4">
                    <Check size={40} className="text-green-500 mx-auto mb-2" />
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Your Plan is Ready!</h3>
                  </div>

                  {/* Macros Summary */}
                  {generatedPlan.daily_macros && (
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-700 dark:to-gray-700 rounded-xl p-4">
                      <h4 className="font-bold text-sm text-gray-800 dark:text-white mb-3">Daily Targets</h4>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div>
                          <div className="text-lg font-bold text-[#FF6600]">{generatedPlan.daily_macros.calories}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Calories</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-blue-600">{generatedPlan.daily_macros.protein_g}g</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Protein</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-green-600">{generatedPlan.daily_macros.carbs_g}g</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Carbs</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-yellow-600">{generatedPlan.daily_macros.fats_g}g</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Fats</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Workout Preview */}
                  {generatedPlan.weekly_workout_plan && (
                    <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-xl p-4">
                      <h4 className="font-bold text-sm text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                        <Dumbbell size={14} className="text-[#FF6600]" /> Weekly Workout
                      </h4>
                      <div className="space-y-1.5">
                        {Object.entries(generatedPlan.weekly_workout_plan).slice(0, 4).map(([day, workout]) => (
                          <div key={day} className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-300 capitalize">{day.replace('_', ' ')}</span>
                            <span className="font-medium text-gray-800 dark:text-gray-100 dark:text-gray-200 text-xs">{workout.name || workout.focus}</span>
                          </div>
                        ))}
                        {Object.keys(generatedPlan.weekly_workout_plan).length > 4 && (
                          <p className="text-xs text-gray-400 text-center">+ {Object.keys(generatedPlan.weekly_workout_plan).length - 4} more days</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tips */}
                  {generatedPlan.tips && generatedPlan.tips.length > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4">
                      <h4 className="font-bold text-sm text-blue-800 dark:text-blue-300 mb-2">Pro Tips</h4>
                      <ul className="space-y-1">
                        {generatedPlan.tips.slice(0, 3).map((tip, i) => (
                          <li key={i} className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
                            <span className="text-blue-500 mt-0.5">&#x2022;</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={handleComplete}
                    data-testid="go-to-dashboard-btn"
                    className="w-full py-3.5 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white font-bold rounded-xl hover:shadow-lg transition-all text-base">
                    Go to Dashboard
                  </button>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-xl text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        {step < 4 && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              {step > 1 ? (
                <button onClick={() => setStep(step - 1)}
                  className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-200 text-sm font-medium transition-colors"
                  data-testid="back-step-btn">
                  <ChevronLeft size={16} /> Back
                </button>
              ) : <div />}
              <button onClick={handleNext}
                className="flex items-center gap-1 px-6 py-2.5 bg-[#FF6600] text-white rounded-xl font-semibold text-sm hover:bg-[#E55C00] transition-colors"
                data-testid="next-step-btn">
                {step === 3 ? 'Save & Continue' : 'Next'} <ChevronRight size={16} />
              </button>
            </div>
            {onLogout && (
              <div className="mt-3 text-center">
                <button onClick={onLogout} data-testid="profile-setup-logout"
                  className="text-gray-400 text-xs hover:text-red-500 transition-colors">
                  Log out
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hamburger drawer */}
      {menuOpen && (
        <div
          data-testid="profile-setup-menu-drawer"
          className="fixed inset-0 z-[10000] flex"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMenuOpen(false)}
          />
          <div className="relative bg-white dark:bg-gray-900 w-72 max-w-[80%] h-full shadow-2xl flex flex-col animate-slide-in-left">
            <div className="bg-gradient-to-r from-[#FF6600] to-[#FF8533] p-4 flex items-center justify-between">
              <h2 className="text-white font-bold text-lg">Menu</h2>
              <button
                data-testid="profile-setup-menu-close"
                onClick={() => setMenuOpen(false)}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 p-3 space-y-1">
              {step > 1 && (
                <button
                  data-testid="profile-setup-menu-back"
                  onClick={() => { setStep(step - 1); setMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <ArrowLeft size={18} />
                  <span className="font-medium">Previous step</span>
                </button>
              )}
              <div className="px-3 py-2 text-xs text-gray-400 uppercase tracking-wide">Progress</div>
              <div className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                Step <strong className="text-[#FF6600]">{step}</strong> of <strong>4</strong>
              </div>
              <div className="border-t border-gray-100 dark:border-gray-800 my-2" />
              {onLogout && (
                <button
                  data-testid="profile-setup-menu-logout"
                  onClick={() => { setMenuOpen(false); onLogout(); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                >
                  <LogOut size={18} />
                  <span className="font-medium">Log out</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSetup;
